import { useCallback, useEffect } from 'react';
import { ButtonBase, Typography } from '@material-ui/core';
import cx from 'classnames';
import PropTypes from 'prop-types';

import { PersonaAvatar } from 'src/components/PersonaAvatar';
import { PersonaButtonSkeleton } from 'src/components/PersonaButtonSkeleton';
import { TruncatedTypography } from 'src/components/TruncatedTypography';

import { useStyles } from './useStyles';

export const PersonaButton = ({
  className,
  classes,
  data,
  onClick,
  onMouseEnter,
  onMouseLeave,
  selected,
  disabled,
  opaqueBackground,
  CustomAvatar,
  onSeen,
  isLoading,
}) => {
  const styles = useStyles({ classes });

  useEffect(() => {
    if (data.id && data.isSuggested) {
      // mark suggested as seen once rendered
      onSeen(data.id);
    }
  }, [data, onSeen]);

  const handleClick = useCallback(
    (event) => {
      onClick(event, data);
    },
    [onClick, data]
  );

  const handleMouseEnter = useCallback(() => {
    if (onMouseEnter) onMouseEnter(data);
  }, [onMouseEnter, data]);

  const handleMouseLeave = useCallback(() => {
    if (onMouseLeave) onMouseLeave(data);
  }, [onMouseLeave, data]);

  const Avatar = CustomAvatar || PersonaAvatar;

  const { isSuggested, isPlaceholder } = data;
  if (isPlaceholder || (isSuggested && isLoading)) {
    return <PersonaButtonSkeleton className={cx(styles.root, className)} />;
  }

  return (
    <ButtonBase
      className={cx(styles.root, className, { [styles.selected]: selected })}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      disabled={disabled}
      data-testid="personaButton"
    >
      <Avatar
        className={styles.avatar}
        avatarType={data.avatarType}
        opaqueBackground={opaqueBackground}
      />

      <div className={styles.textWrapper}>
        <TruncatedTypography className={cx(styles.text, { [styles.selectedText]: selected })}>
          {data.name}
        </TruncatedTypography>

        {data.isSuggested && <Typography className={styles.suggested}>Suggested</Typography>}
      </div>
    </ButtonBase>
  );
};

PersonaButton.propTypes = {
  className: PropTypes.string,
  data: PropTypes.shape().isRequired,
  onClick: PropTypes.func.isRequired,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  opaqueBackground: PropTypes.bool,
  CustomAvatar: PropTypes.object,
  classes: PropTypes.object,
  onMouseEnter: PropTypes.func,
  onMouseLeave: PropTypes.func,
  onSeen: PropTypes.func,
};

PersonaButton.defaultProps = {
  className: '',
  selected: false,
  disabled: false,
  opaqueBackground: false,
  CustomAvatar: null,
  classes: null,
  onMouseEnter: null,
  onMouseLeave: null,
  onSeen: null,
  isLoading: false,
};
